import { Check, Lock, Mail, Phone, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CardWrapper } from '~/components/cards/CardWrapper';
import { formatPhoneNumber } from '~/components/ui/inputs/phoneNumber';
import { cn } from '~/utils';
import { contactLabel, type ContactDetails } from '~/utils/contact';

export type { ContactDetails } from '~/utils/contact';

/** Fills its parent; the caller sizes the wrapper. */
export function ContactCard({
  contact,
  compact = false,
  primary = false,
  onPrimaryChange,
  selected = false,
  onSelectedChange,
  selectDisabled = false,
  actedLabel,
}: {
  contact: ContactDetails;
  compact?: boolean;
  primary?: boolean;
  onPrimaryChange?: (primary: boolean) => void;
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  selectDisabled?: boolean;
  actedLabel?: string;
}) {
  const { t } = useTranslation('general');
  const name = contactLabel(contact);
  const primaryGroup = primary
    ? {
        role: 'group',
        'aria-label': t('contact_card.primary_contact_aria_label', { name }),
      }
    : {};
  const primaryBadge = primary && !onPrimaryChange && (
    <span className="inline-block rounded bg-prim-700 px-1.5 py-0.5 text-[0.625rem] font-semibold uppercase leading-none text-neut-0">
      {t('contact_card.primary_badge')}
    </span>
  );
  const selectable = onSelectedChange !== undefined;
  const highlighted = selectable && selected;
  const selectInput = selectable && (
    <input
      type="checkbox"
      value={contact.id}
      aria-label={name}
      checked={selected}
      disabled={selectDisabled}
      onChange={() => onSelectedChange(!selected)}
      className="absolute inset-0 z-1 m-0 h-full w-full cursor-pointer appearance-none rounded-lg disabled:cursor-default"
    />
  );
  const pill = onPrimaryChange && (
    <button
      type="button"
      aria-pressed={primary}
      aria-label={t('contact_card.primary_pill_aria_label', { name })}
      onClick={() => onPrimaryChange(!primary)}
      className={cn(
        'relative z-2 inline-flex min-h-6 shrink-0 items-center gap-1 rounded-md border px-2.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-prim focus-visible:ring-offset-2',
        primary
          ? 'border-prim-700 bg-prim-700 text-neut-0'
          : 'border-neut-border bg-neut-bg text-neut-prim hover:bg-neut-hover'
      )}
    >
      {primary && <Check size={12} strokeWidth={3} aria-hidden />}
      {t('contact_card.primary_badge')}
    </button>
  );
  const status = pill && (
    <div className="flex shrink-0 flex-col items-end">{pill}</div>
  );
  const acted = actedLabel && <p className="text-neut-sec">{actedLabel}</p>;
  const lockBadge = selectable && selectDisabled && (
    <span className="absolute -bottom-0.5 -left-0.5 flex h-4 w-4 items-center justify-center rounded-full border border-neut-border bg-neut-bg">
      <Lock size={10} className="text-neut-sec" aria-hidden />
    </span>
  );
  const detail = (href: string, text: string, className = '') =>
    selectable ? (
      <span className={cn('text-neut-prim', className)}>{text}</span>
    ) : (
      <a href={href} className={cn('text-prim-500 hover:underline', className)}>
        {text}
      </a>
    );
  const details = (
    <>
      {contact.phoneNumber && (
        <p className={`flex items-center ${compact ? 'gap-1' : 'gap-2 pt-1'}`}>
          <Phone
            size={compact ? 11 : 14}
            className="text-neut-sec shrink-0"
            aria-hidden
          />
          {detail(
            `tel:${contact.phoneNumber}`,
            formatPhoneNumber(contact.phoneNumber)
          )}
        </p>
      )}
      {contact.externalId && (
        <p
          className={`flex items-center min-w-0 ${compact ? 'gap-1' : 'gap-2'}`}
        >
          <Mail
            size={compact ? 11 : 14}
            className="text-neut-sec shrink-0"
            aria-hidden
          />
          {detail(
            `mailto:${contact.externalId}`,
            contact.externalId,
            'break-all min-w-0'
          )}
        </p>
      )}
    </>
  );

  if (compact) {
    return (
      <div
        {...primaryGroup}
        className={cn(
          'flex items-start gap-2 rounded-md border bg-neut-bg px-2 py-1.5 text-xs text-left min-w-0',
          highlighted
            ? 'border-prim-700 ring-1 ring-prim-700'
            : 'border-neut-border',
          selectable
            ? 'relative h-full has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-prim-500'
            : undefined
        )}
      >
        {selectInput}
        <div className="relative w-6 h-6 rounded-full bg-prim-50 flex items-center justify-center shrink-0">
          <User size={12} className="text-prim-500" aria-hidden />
          {lockBadge}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <p className="text-neut-prim font-semibold">{name}</p>
            {primaryBadge}
          </div>
          {details}
          {acted}
        </div>
        {status}
      </div>
    );
  }

  return (
    <CardWrapper
      padding={selectable ? 'sm' : 'lg'}
      className={cn(
        'min-w-0',
        highlighted ? 'ring-2 ring-prim-700' : undefined,
        selectable
          ? 'relative h-full has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-prim-500 has-[input:enabled]:hover:border-prim-500'
          : undefined
      )}
    >
      {selectInput}
      <div {...primaryGroup} className="flex items-start gap-3 text-sm">
        <div className="relative w-10 h-10 rounded-full bg-prim-50 flex items-center justify-center shrink-0">
          <User size={20} className="text-prim-500" aria-hidden />
          {lockBadge}
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h2 className="text-neut-prim font-semibold">{name}</h2>
            {primaryBadge}
          </div>
          {details}
          {acted}
        </div>
        {status}
      </div>
    </CardWrapper>
  );
}
